import type { GetPromptResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

/**
 * IDs of the priority default prompts that should appear first among system prompts.
 * These are shown in this specific order at the top of the system prompts section.
 */
const PRIORITY_SYSTEM_PROMPT_IDS = [
	'sys_01dd8573ce', // Analyze customer feedback trends
	'sys_3db52cef86', // Brainstorm ideas for project
	'sys_97d5b17ce5', // Send Slack message of weekly progress
];

/**
 * Pattern to detect skill tag syntax in prompts (e.g., '/get-data-insights').
 * Skills prompts contain slash commands that invoke Rovo skills.
 */
const SKILL_TAG_PATTERN = /'\/([\w-]+)'/;

/**
 * Checks if a prompt contains skill tag syntax.
 */
function promptContainsSkillTag(prompt: string): boolean {
	return SKILL_TAG_PATTERN.test(prompt);
}

function isSystemPromptCheck(prompt: GetPromptResponse): boolean {
	if (prompt.id.startsWith('sys_')) {
		return true;
	}

	if (prompt.creator?.id === 'system' || prompt.creator?.id === 'system-prompts') {
		return true;
	}

	return prompt.isSystemPrompt === true;
}

/**
 * Determines the sort priority category for a prompt.
 * Lower numbers = higher priority (shown first).
 *
 * Priority order:
 * 1. User-generated prompts (non-system)
 * 2. Priority system prompts (specific IDs)
 * 3. System prompts with appIds (3P app connectors)
 * 4. System prompts with skill tags
 * 5. Other system prompts
 */
function getPromptSortCategory(prompt: GetPromptResponse): number {
	// User-generated prompts first
	if (!isSystemPromptCheck(prompt)) {
		return 1;
	}

	// Priority system prompts
	if (PRIORITY_SYSTEM_PROMPT_IDS.includes(prompt.id)) {
		return 2;
	}

	// System prompts with appIds (3P connectors)
	if (prompt.appIds && prompt.appIds.length > 0) {
		return 3;
	}

	// System prompts with skill tags
	if (promptContainsSkillTag(prompt.prompt)) {
		return 4;
	}

	// Other system prompts
	return 5;
}

/**
 * Gets the index of a priority system prompt for secondary sorting.
 * Returns a high number for non-priority prompts.
 */
function getPriorityPromptIndex(prompt: GetPromptResponse): number {
	const index = PRIORITY_SYSTEM_PROMPT_IDS.indexOf(prompt.id);
	return index >= 0 ? index : PRIORITY_SYSTEM_PROMPT_IDS.length;
}

/**
 * Sorts prompts according to the following order:
 *
 * 1. Favorited prompts first (across all categories)
 * 2. Then non-favorited prompts in category order:
 *    a. User-generated prompts (non-system prompts) - sorted by createdAt desc
 *    b. Priority system prompts (Analyze customer feedback, Brainstorm ideas, Send Slack message)
 *    c. System prompts with appIds (connected to 3P apps like Salesforce, Google Drive)
 *    d. System prompts with skill tags (those invoking Rovo skills like '/get-data-insights')
 *    e. Other system prompts
 *
 * Within each category, prompts are sorted by createdAt descending (newest first).
 *
 * @param prompts - Array of prompts from the API response
 * @returns Sorted array of prompts
 */
export function sortPrompts(prompts: GetPromptResponse[]): GetPromptResponse[] {
	return [...prompts].sort((a, b) => {
		// Favorites always come first
		if (a.isFavorited !== b.isFavorited) {
			return a.isFavorited ? -1 : 1;
		}

		const categoryA = getPromptSortCategory(a);
		const categoryB = getPromptSortCategory(b);

		// Sort by category
		if (categoryA !== categoryB) {
			return categoryA - categoryB;
		}

		// Within category 2 (priority system prompts), sort by predefined order
		if (categoryA === 2) {
			return getPriorityPromptIndex(a) - getPriorityPromptIndex(b);
		}

		// Within other categories, sort by createdAt descending (newest first)
		const createdAtA = a.createdAt ?? 0;
		const createdAtB = b.createdAt ?? 0;
		return createdAtB - createdAtA;
	});
}
